import angular from 'angular';
import csmCommonWeb from 'csm-common-web';
import storeItemsTemplate from './storeItems.template.html';
import './storeItems.css';

export default angular
  .module('books.ui.storeItems', [csmCommonWeb])
  .component('storeItems', StoreItemsComponent()).name;

/*****************************************************************************
 * StoreItemsComponent */
/**
 * @ngdoc   component
 * @module  books.ui.storeItems
 * @name    books.ui.storeItems.component:storeItems
 *
 * @param {array}   items            List of items in a webStore.
 * @param {array}   source           List of ProductListItem objects.
 * @param {boolean} hideFeatured     Optional, hides/shows the features tabs.
 * @param {string}  storeType        Store type
 * @param {string}  infoBlock        Optional, informational text to be displayed
 *                                   on the template.
 * @param {boolean} disableOrdering  Optional, allows/prevents re-ordering the
 *                                   store items.
 *
 * @description
 * This component displays the selected available items, store items, and
 * featured items (if selected), in lists using the shuttleList directive.
 *
 * The items can be moved between lists and ordered in the store and featured
 * lists by dragging the item using the drag handle, clicking on the move
 * button or using the arrow keys on the keyboard.
 *
 * Using the Ctrl button along with the arrow keys will move the item and using
 * the arrow keys without the Ctrl button will move the focus. Since the
 * shuttleList directive requires all arrays to be the same, the **source**
 * array is used for displaying in the DOM. The **items** array is updated as
 * necessary because that is the array that gets saved to the server.
 *****************************************************************************/
function StoreItemsComponent() {
  return {
    bindings: {
      items: '=',
      source: '<',
      hideFeatured: '<?',
      storeType: '<',
      infoBlock: '<?',
      disableOrdering: '<?'
    },
    template: storeItemsTemplate,
    controller: StoreItemsController
  };
}

/*****************************************************************************
 * Controller: StoreItemsController
 *
 * Controls the handling of items between the four lists. Individual arrays
 * will control the actual data for those lists, but a fifth array will be the
 * full collection of store items to save to the server.
 *****************************************************************************/
/* @ngInject */
function StoreItemsController($scope, ShuttleService, UserAgent) {
  /** Self Reference. */ var mThis = this;

  mThis.$onInit = function () {
    /** Boolean to display the infoBlock on the template */
    mThis.displayInfoBlock = !!mThis.infoBlock;

    /*-----------------------------------------------------------*/
    /* If the featured items are being shown, initialize the     */
    /* "collapsed" flags so the storeItems are open. Then create */
    /* the handler functions for opening each uib-collapse.      */
    /*-----------------------------------------------------------*/
    if (!mThis.hideFeatured) {
      mThis.storeItemsCollapsed = false;
      mThis.featuredItemsCollapsed = true;

      /*************************************************************************
       * openCollapse */
      /**
       * Expands the selected items tab and closes the others.
       *
       * @param  openFeatured  true  = Switches the open section to Featured
       *                       false = Switches the open section to Store
       *************************************************************************/
      mThis.openCollapse = function (openFeatured) {
        mThis.storeItemsCollapsed = openFeatured;
        mThis.featuredItemsCollapsed = !openFeatured;
      };
    }
  };

  /** Unique ID names specific to the scope. */
  mThis.ids = {
    availableItems: 'availableItems-' + $scope.$id,
    webstoreItems: 'webstoreItems-' + $scope.$id,
    nonFeatured: 'nonFeatured-' + $scope.$id,
    featured: 'featured-' + $scope.$id
  };

  /** Unique group names specific to the scope. */
  mThis.groups = {
    featured: 'featuredGroup-' + $scope.$id,
    items: 'itemsGroup-' + $scope.$id
  };

  /*-----------------*/
  /* Data providers. */
  /*-----------------*/
  /** Products not yet assigned to store. */ mThis.availableProducts = [];
  /** Products that have been assigned. */ mThis.assignedItems = [];
  /** Assigned items to be featured. */ mThis.featuredItems = [];
  /** Assigned items not featured. */ mThis.nonFeaturedItems = [];

  /*---------------------------------------------------------------*/
  /* If the user is on a mobile device, we need to enable the      */
  /* touch drag and drop feature on the drag handle. Only the drag */
  /* handle is set so that the user can scroll through the lists.  */
  /*---------------------------------------------------------------*/
  mThis.mobileDevice = UserAgent.isIOSDevice() || UserAgent.isAndroid();

  /***************************************************************************
   * cmdHandler */
  /**
   * Shuttle service for the featured and non-featured items.
   *
   * @param  cmd     Shuttle command object.
   * @param  source  Source shuttle-list controller.
   * @param  target  Target shuttle-list controller.
   ***************************************************************************/
  ShuttleService.group(mThis.groups.featured).cmdHandler = function (
    cmd,
    source,
    target
  ) {
    /* Source array */ var sourceData = cmd.sourceCtrl.data;
    /* Index of source element */ var sourceIndex = cmd.sourceCtrl.findIndex(
      cmd.sourceElem
    );
    /* Source object */ var sourceObj = sourceData[sourceIndex];

    /* Source list is non-featured */ var nonFeaturedSource =
      source.id == mThis.ids.nonFeatured;
    /* Source list is featured */ var featuredSource =
      source.id == mThis.ids.featured;

    /*---------------------------------------------------------*/
    /* Check the source and target id's. If they are different */
    /* the item is being moved from one list to the other.     */
    /*---------------------------------------------------------*/
    if (source.id != target.id) {
      /*--------------------------------------------------*/
      /* Move the item on click or when the left or right */
      /* arrows are used along with the control key.      */
      /*--------------------------------------------------*/
      if (cmd.click || (cmd.ctrlKey && (cmd.right || cmd.left))) {
        cmd.handler.shuttleItem();
        moveFeatured(sourceObj);

        if (nonFeaturedSource) setFeaturedSort();
      }

      /*-----------------------------------------*/
      /* If the left or right arrows are pressed */
      /* without the ctrl key, move the focus.   */
      /*-----------------------------------------*/
      if (!cmd.ctrlKey && (cmd.right || cmd.left))
        return cmd.handler.shuttleFocus(0);
    }

    /*---------------------------------------------------------*/
    /* Move the item or the cursor when the up and down arrows */
    /* are used, regardless of the target and source id's.     */
    /*---------------------------------------------------------*/
    if (cmd.vertical)
      return cmd.ctrlKey
        ? (cmd.handler.moveItem(), setFeaturedSort())
        : cmd.handler.moveFocus();

    /*--------------------------------------------------*/
    /* Allow items to be moved on dragging or dropping. */
    /*--------------------------------------------------*/
    if (cmd.dragStart) return cmd.handler.startDrag('move');
    if (cmd.dragOver) return cmd.handler.allowDrop('move');

    /*---------------------------------------------------*/
    /* Add the insert class when entering a new element. */
    /*---------------------------------------------------*/
    if (cmd.dragEnter) return !!cmd.dropElement.addClass('insert');

    if (cmd.dragLeave || cmd.drop) {
      /*-----------------------------------------*/
      /* Remove the insert class when an element */
      /* is dropped or it leaves the source.     */
      /*-----------------------------------------*/
      cmd.dropElement.removeClass('insert');

      /*---------------------------------------------------------*/
      /* Move the element when dropped. Prevent default is used  */
      /* here to prevent Firefox from re-routing after the drop. */
      /*---------------------------------------------------------*/
      if (cmd.drop) {
        cmd.event.preventDefault();

        /*-----------------------------------------------------*/
        /* Remove the 'insert' class from all elements that    */
        /* have it. This is primarily for FireFox as it does   */
        /* not always remove the class on the dragLeave event. */
        /*-----------------------------------------------------*/
        removeInsertClass();

        /*------------------------------------------------------------------*/
        /* If the source and target are not the same, move the element and  */
        /* update the appropriate lists. Prevent default is used to prevent */
        /* Firefox from re-routing after the drop when an anchor is used.   */
        /*------------------------------------------------------------------*/
        if (source.id != target.id) {
          cmd.handler.drop(-1);
          moveFeatured(sourceObj);

          /*-----------------------------------------------------*/
          /* If being moved to the featured list, sort the list. */
          /*-----------------------------------------------------*/
          if (nonFeaturedSource) setFeaturedSort();
        }

        /*-------------------------------------*/
        /* If moving within the featured list, */
        /* move the item and sort the list.    */
        /*-------------------------------------*/
        if (source.id == target.id && featuredSource) {
          cmd.handler.drop(-1);
          setFeaturedSort();
        }
      }
      return true;
    }
  };

  /*----------------------------------------------------------*/
  /* Shuttle service to move items to and from the web store. */
  /*----------------------------------------------------------*/
  ShuttleService.group(mThis.groups.items).cmdHandler = function (
    cmd,
    source,
    target
  ) {
    /* Source array */ var sourceData = cmd.sourceCtrl.data;
    /* Index of source element */ var sourceIndex = cmd.sourceCtrl.findIndex(
      cmd.sourceElem
    );
    /* Source object */ var sourceObj = sourceData[sourceIndex];

    /* Source list of webStore */ var storeSource =
      source.id == mThis.ids.webstoreItems;
    /* Source list of items */ var availableProducts =
      source.id == mThis.ids.availableItems;

    /*---------------------------------------------------------*/
    /* Check the source and target id's. If they are different */
    /* the item is being moved from one list to the other.     */
    /*---------------------------------------------------------*/
    if (source.id != target.id) {
      /*--------------------------------------------------*/
      /* Move the item on click or when the left or right */
      /* arrows are used along with the control key.      */
      /*--------------------------------------------------*/
      if (cmd.click || (cmd.ctrlKey && (cmd.right || cmd.left))) {
        moveProduct(sourceObj);
        cmd.handler.shuttleItem();

        /*-------------------------------------------------------------------*/
        /* Add the item to the web store array when moving to the web store. */
        /*-------------------------------------------------------------------*/
        if (availableProducts) addStoreItem(sourceObj);

        /*------------------------------------*/
        /* Remove the item from the web store */
        /* array when removing from the list. */
        /*------------------------------------*/
        if (storeSource) {
          var wsi = mThis.items.find(function (wi) {
            return wi.itemID == sourceObj.id;
          });
          mThis.items.removeItem(wsi);
        }
      }

      /*-----------------------------------------*/
      /* If the left or right arrows are pressed */
      /* without the ctrl key, move the focus.   */
      /*-----------------------------------------*/
      if (!cmd.ctrlKey && (cmd.right || cmd.left))
        return cmd.handler.shuttleFocus(0);
    }

    /*---------------------------------------------------------*/
    /* Move the item or the cursor when the up and down arrows */
    /* are used, regardless of the target and source id's.     */
    /*---------------------------------------------------------*/
    if (cmd.vertical)
      return cmd.ctrlKey
        ? (cmd.handler.moveItem(), setStoreSort())
        : cmd.handler.moveFocus();

    /*--------------------------------------------------*/
    /* Allow items to be moved on dragging or dropping. */
    /*--------------------------------------------------*/
    if (cmd.dragStart) return cmd.handler.startDrag('move');
    if (cmd.dragOver) return cmd.handler.allowDrop('move');

    /*---------------------------------------------------*/
    /* Add the insert class when entering a new element. */
    /*---------------------------------------------------*/
    if (cmd.dragEnter) return !!cmd.dropElement.addClass('insert');

    /*-----------------------------------------*/
    /* Remove the insert class when an element */
    /* is dropped or it leaves the source.     */
    /*-----------------------------------------*/
    if (cmd.dragLeave || cmd.drop) {
      cmd.dropElement.removeClass('insert');

      if (cmd.drop) {
        cmd.event.preventDefault();

        /*-----------------------------------------------------*/
        /* Remove the 'insert' class from all elements that    */
        /* have it. This is primarily for FireFox as it does   */
        /* not always remove the class on the dragLeave event. */
        /*-----------------------------------------------------*/
        removeInsertClass();

        /*------------------------------------------------------------------*/
        /* If the source and target are not the same, move the element and  */
        /* update the appropriate lists. Prevent default is used to prevent */
        /* Firefox from re-routing after the drop when an anchor is used.   */
        /*------------------------------------------------------------------*/
        if (source.id != target.id) {
          cmd.handler.drop(-1);
          moveProduct(sourceObj);

          /*-------------------------------------*/
          /* If moving from the webStore, remove */
          /* the item from the webStore list.    */
          /*-------------------------------------*/
          if (storeSource) {
            var wsi = mThis.items.find(function (wi) {
              return wi.itemID == sourceObj.id;
            });
            mThis.items.removeItem(wsi);
          }
        }

        /*----------------------------------------------*/
        /* If we are dropping within the webStore items */
        /* list, drop the item and resort the list.     */
        /*----------------------------------------------*/
        if (source.id == target.id && storeSource) {
          cmd.handler.drop(-1);
          cmd.event.preventDefault();
          setStoreSort();
        }

        /*----------------------------------------------*/
        /* If moving from available source to web     */
        /* store, add the object to the web store list. */
        /*----------------------------------------------*/
        if (source.id != target.id && availableProducts)
          addStoreItem(sourceObj);
      }
      return true;
    }
  };

  /*---------------------------------------------*/
  /* Watch for the source and store items to   */
  /* be loaded and call the setSources function. */
  /*---------------------------------------------*/
  $scope.$watch(function () {
    return mThis.source;
  }, setSources);
  $scope.$watch(function () {
    return mThis.items;
  }, setSources);

  /***************************************************************************
   * addStoreItem */
  /**
   * Create a storeItem object from the source object. If this source object
   * was in the list of assigned items, then add it to the list, and sort the
   * list.
   *
   * @param  sourceObj  ProductListItem object being added.
   ***************************************************************************/
  function addStoreItem(sourceObj) {
    var availableIndex = mThis.assignedItems.indexOf(sourceObj);
    if (availableIndex > -1) {
      var storeItem = {
        featureSort: 0,
        featured: false,
        itemID: sourceObj.id,
        name: sourceObj.name,
        sort: availableIndex + 1,
        storeType: mThis.storeType
      };

      mThis.items.push(storeItem);
      setStoreSort();
    }
  }

  /***************************************************************************
   * moveFeatured
   *
   * Update the featured and featureSort properties of the corresponding item
   * in the **items** list from the **sourceObj**.
   *
   * @param  sourceObj  ProductListItem being moved.
   ***************************************************************************/
  function moveFeatured(sourceObj) {
    var featIndex = mThis.featuredItems.indexOf(sourceObj);

    mThis.items.some(function (item) {
      if (item.itemID == sourceObj.id) {
        item.featured = featIndex > -1;
        item.featureSort = featIndex + 1;

        return true;
      }
    });
  }

  /***************************************************************************
   * moveProduct */
  /**
   * Updates the featured items and non featured items lists as appropriate when
   * an item is being added to or removed from the store items list. This
   * is called prior to the item being moved.
   *
   * @param  sourceObj  ProductListItem object being moved.
   ***************************************************************************/
  function moveProduct(sourceObj) {
    /*---------------------------------------------------*/
    /* If this product is in the available source, then  */
    /* we need to add it to the non-featured items list. */
    /*---------------------------------------------------*/
    if (mThis.availableProducts.indexOf(sourceObj) > -1)
      mThis.nonFeaturedItems.push(sourceObj);
    /*-------------------------------------------------*/
    /* Otherwise, the item is being moved into the     */
    /* available source list, and we need to remove it */
    /* from both the featured, and non-featured lists. */
    /*-------------------------------------------------*/ else {
      mThis.featuredItems.removeItem(sourceObj);
      mThis.nonFeaturedItems.removeItem(sourceObj);
    }
  }

  /***************************************************************************
   * removeInsertClass
   *
   * Called on drop. Removes the insert class from all elements.
   ***************************************************************************/
  function removeInsertClass() {
    /*--------------------------------------------------------*/
    /* The querySelectorAll returns a NodeList, not an array. */
    /* We need to use the forEach from the Array.prototype    */
    /* directly because the, while NodeList has the forEach   */
    /* function, it is not supported in all browsers.         */
    /*--------------------------------------------------------*/
    var elements = document.querySelectorAll('store-items .insert');
    Array.prototype.forEach.call(elements, function (e) {
      e.classList.remove('insert');
    });
  }

  /***************************************************************************
   * setFeaturedSort
   *
   * Assign the featureSort property on each item to the order displayed.
   ***************************************************************************/
  function setFeaturedSort() {
    for (var i = 0; i < mThis.featuredItems.length; i++) {
      var fi = mThis.items.find(function (wi) {
        return wi.itemID == mThis.featuredItems[i].id;
      });
      fi.featureSort = i + 1;
    }
  }

  /***************************************************************************
   * setSources */
  /**
   * Initialize the arrays to be used by the DOM. If the item exists in the
   * items list, then we need to remove it from the available list and add it
   * to the assigned list. Likewise, add the item to the corresponding
   * featured/non-featured list depending on the featured flag.
   ***************************************************************************/
  function setSources() {
    /*------------------------------------------------------------*/
    /* We can't set any of the lists if we don't have the source. */
    /*------------------------------------------------------------*/
    if (!mThis.source) return;

    /*----------------------------------------*/
    /* Create the web store product, featured */
    /* items and non featured item arrays.    */
    /*----------------------------------------*/
    mThis.availableProducts = mThis.source.concat();
    mThis.assignedItems = [];
    mThis.featuredItems = [];
    mThis.nonFeaturedItems = [];

    /*-----------------------------------------------------*/
    /* If the items list doesn't exist, set it to an       */
    /* empty array and skip the remainder of the function. */
    /*-----------------------------------------------------*/
    if (!mThis.items) {
      mThis.items = [];
      return;
    }

    /*-----------------------------------------------------------------------*/
    /* Populate the featured and non-featured lists using the values of the  */
    /* items in the items list, for every product in the availableSorce      */
    /* list. We are traversing the availableProducts list in reverse order   */
    /* so that if we need to move the product from the available list to     */
    /* the featured list, we don't disrupt the index order while traversing. */
    /*-----------------------------------------------------------------------*/
    for (var i = mThis.availableProducts.length - 1; i > -1; i--) {
      var wsi = mThis.items.find(function (wi) {
        return wi.itemID == mThis.availableProducts[i].id;
      });

      /*--------------------------------------------------*/
      /* If no match was found, or the items list is      */
      /* empty, there is nothing to add for this product. */
      /*--------------------------------------------------*/
      if (wsi) {
        var product = mThis.availableProducts.splice(i, 1).shift();
        product.sort = wsi.sort;
        product.featureSort = wsi.featureSort;

        /*-------------------------------------------*/
        /* This item has been assigned to the store. */
        /*-------------------------------------------*/
        mThis.assignedItems.push(product);

        /*------------------------------------------------------------------*/
        /* If the item is featured, we want to add it to the featured list. */
        /*------------------------------------------------------------------*/
        if (wsi.featured) mThis.featuredItems.push(product);
        /*-------------------------------------------------*/
        /* Otherwise, it will be in the non-featured list. */
        /*-------------------------------------------------*/ else
          mThis.nonFeaturedItems.push(product);
      }
    }

    /*-----------------------------------------------------------*/
    /* Sort the assignedItems to be displayed by the sort order. */
    /*-----------------------------------------------------------*/
    mThis.assignedItems.sort(function (a, b) {
      return mThis.disableOrdering
        ? a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        : a.sort - b.sort;
    });

    /*-------------------------------------------------------------*/
    /* Sort the featured items to be displayed by the featureSort. */
    /*-------------------------------------------------------------*/
    mThis.featuredItems.sort(function (a, b) {
      return a.featureSort - b.featureSort;
    });
  }

  /***************************************************************************
   * setStoreSort
   *
   * Assign the sort property on each item to the order displayed.
   ***************************************************************************/
  function setStoreSort() {
    for (var i = 0; i < mThis.assignedItems.length; i++) {
      if (mThis.disableOrdering)
        mThis.assignedItems.sort(function (a, b) {
          return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
        });
      else {
        var wsi = mThis.items.find(function (wi) {
          return wi.itemID == mThis.assignedItems[i].id;
        });
        wsi.sort = i + 1;
      }
    }
    $scope.$digest();
  }
}
